import { Link } from "react-router-dom";

export const Terms = () => {
  return (
    <div style={{
      maxWidth: "780px",
      margin: "0 auto",
      padding: "4rem 2rem",
      color: "#374151",
      lineHeight: "1.7"
    }}>
      <h1 style={{
        fontSize: "2.25rem",
        fontWeight: "800",
        color: "#111827",
        marginBottom: "0.5rem",
        lineHeight: "1.2"
      }}>
        Terms of Service
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "2.5rem" }}>
        <strong>Effective date:</strong> April 26, 2026
      </p>

      <Section title="1. Acceptance of these Terms">
        <p>
          These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) form a
          binding agreement between you and SHOWME Campaign Finances
          (&ldquo;<strong>SHOWME</strong>,&rdquo; &ldquo;we,&rdquo; or
          &ldquo;us&rdquo;) governing your access to and use of the website
          {" "}<a href="https://showmecampaignfinances.com">showmecampaignfinances.com</a>{" "}
          and the related software-as-a-service application (collectively, the
          &ldquo;<strong>Service</strong>&rdquo;). By creating an account or
          using the Service, you agree to these Terms and to our{" "}
          <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do
          not use the Service.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          You must be at least 18 years old and legally able to enter into a
          contract to use the Service. By using the Service, you represent
          that you meet these requirements.
        </p>
      </Section>

      <Section title="3. Accounts">
        <p>
          You are responsible for maintaining the confidentiality of your
          account credentials and for all activity that occurs under your
          account. Notify us promptly at{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>{" "}
          if you suspect unauthorized access. We may suspend or terminate
          accounts that violate these Terms.
        </p>
      </Section>

      <Section title="4. The Service">
        <p>
          SHOWME extracts structured data from publicly filed Missouri Ethics
          Commission campaign finance reports (PDFs) and presents the
          extracted data through a web application, including donor and
          expenditure listings, totals, and analytics. The underlying
          documents are public records; the Service adds structure, search,
          and analysis on top of those records.
        </p>
        <p>
          We work to keep extractions accurate but do not guarantee that
          extracted data is free of errors. Always verify against the
          underlying filing for legal, journalistic, or compliance purposes.
        </p>
      </Section>

      <Section title="5. Subscriptions and billing">
        <p>The Service is offered in three tiers:</p>
        <ul style={list}>
          <li>
            <strong>Free.</strong> Three lifetime trial uploads. No payment
            required. No automatic renewal.
          </li>
          <li>
            <strong>Basic ($10/year).</strong> Up to 100 uploads in any
            rolling 365-day period. Billed annually.
          </li>
          <li>
            <strong>Max ($20/year).</strong> Unlimited uploads, full analytics
            dashboard, and Reporting Entity Groups. Billed annually.
          </li>
        </ul>
        <p>
          <strong>Payment.</strong> We use Stripe to process payments. By
          subscribing you authorize us to charge your payment method for the
          subscription fee at the start of each billing period.
        </p>
        <p>
          <strong>Auto-renewal.</strong> Paid subscriptions automatically renew
          for an additional one-year period at the end of each term unless you
          cancel before the renewal date. You can cancel at any time through
          your account settings.
        </p>
        <p>
          <strong>Cancellation.</strong> When you cancel, your subscription
          remains active through the end of the period you have already paid
          for. We do not provide partial refunds for unused time.
        </p>
        <p>
          <strong>Refunds.</strong> All fees are nonrefundable except where
          required by law. If you believe you have been billed in error,
          contact us within 30 days at{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>{" "}
          and we will review the charge in good faith.
        </p>
        <p>
          <strong>Price changes.</strong> We may change subscription pricing
          for future billing periods. We will notify you by email at least 30
          days before any price change affects you. If you do not accept the
          change, you may cancel before it takes effect.
        </p>
        <p>
          <strong>Taxes.</strong> Fees are exclusive of taxes. You are
          responsible for any taxes that apply to your purchases.
        </p>
      </Section>

      <Section title="6. Acceptable use">
        <p>You agree not to:</p>
        <ul style={list}>
          <li>Use the Service for any unlawful purpose;</li>
          <li>
            Upload content you do not have the legal right to upload (note
            that Missouri Ethics Commission filings are public records);
          </li>
          <li>
            Attempt to access another user&rsquo;s account or any non-public
            area of the Service;
          </li>
          <li>
            Interfere with, disrupt, or place an unreasonable load on the
            Service, including by automated scraping or denial-of-service
            attacks;
          </li>
          <li>
            Reverse-engineer, decompile, or attempt to extract the source code
            of the Service, except as expressly permitted by law;
          </li>
          <li>
            Resell, sublicense, or otherwise redistribute access to the
            Service without our written permission.
          </li>
        </ul>
      </Section>

      <Section title="7. Your content">
        <p>
          You retain ownership of any documents you upload. By uploading, you
          grant SHOWME a worldwide, royalty-free license to store, process,
          display, and otherwise use the content as needed to provide the
          Service to you. We do not claim copyright in the underlying
          campaign finance reports, which are public records.
        </p>
      </Section>

      <Section title="8. Our intellectual property">
        <p>
          The Service, including the website, application code, extraction
          logic, branding, and database structure, is owned by SHOWME and is
          protected by intellectual property laws. We grant you a limited,
          non-exclusive, non-transferable, revocable license to use the
          Service for its intended purpose. All rights not expressly granted
          are reserved.
        </p>
      </Section>

      <Section title="9. Service availability">
        <p>
          We aim for high availability but do not guarantee that the Service
          will be uninterrupted, error-free, or available at any specific
          time. We may suspend the Service for maintenance, updates, or
          security reasons.
        </p>
      </Section>

      <Section title="10. Disclaimers">
        <p style={{ textTransform: "uppercase", fontSize: "0.9rem" }}>
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as
          available&rdquo; without warranties of any kind, whether express,
          implied, statutory, or otherwise, including any implied warranties
          of merchantability, fitness for a particular purpose,
          non-infringement, or accuracy. We do not warrant that the Service
          will meet your requirements, that the extracted data is free of
          errors, or that the Service will be uninterrupted or secure.
        </p>
        <p>
          The data presented by the Service is derived from public filings and
          is provided for informational purposes only. It does not constitute
          legal, tax, financial, or compliance advice. You are responsible for
          verifying any data against the underlying filings before relying on
          it.
        </p>
      </Section>

      <Section title="11. Limitation of liability">
        <p style={{ textTransform: "uppercase", fontSize: "0.9rem" }}>
          To the maximum extent permitted by law, in no event will SHOWME, its
          officers, employees, or affiliates be liable for any indirect,
          incidental, special, consequential, exemplary, or punitive damages,
          or for any loss of profits, revenue, data, business opportunities,
          or goodwill, arising out of or in connection with the Service,
          whether based on contract, tort, strict liability, or any other
          legal theory, even if we have been advised of the possibility of
          such damages.
        </p>
        <p style={{ textTransform: "uppercase", fontSize: "0.9rem" }}>
          Our total aggregate liability arising out of or relating to the
          Service in any 12-month period is limited to the greater of (a) the
          amount you paid us during that 12-month period or (b) one hundred
          U.S. dollars ($100).
        </p>
        <p>
          Some jurisdictions do not allow the exclusion or limitation of
          certain damages, so some of the above may not apply to you.
        </p>
      </Section>

      <Section title="12. Indemnification">
        <p>
          You agree to indemnify and hold harmless SHOWME, its officers,
          employees, and affiliates from any claims, losses, liabilities,
          damages, and expenses (including reasonable attorneys&rsquo; fees)
          arising out of or related to your violation of these Terms, your
          misuse of the Service, or your violation of any third-party right.
        </p>
      </Section>

      <Section title="13. Termination">
        <p>
          You may terminate your account at any time through your account
          settings or by emailing us. We may suspend or terminate your account
          if you breach these Terms or if we are required to do so by law.
          Sections 7 through 12, 14, and 16 survive termination.
        </p>
      </Section>

      <Section title="14. Governing law and disputes">
        <p>
          These Terms are governed by the laws of the State of Missouri,
          excluding its conflict-of-laws rules. Any dispute arising out of or
          relating to these Terms or the Service will be resolved exclusively
          in the state or federal courts located in Missouri, and you consent
          to the personal jurisdiction of those courts.
        </p>
        <p>
          Nothing in this section prevents either party from seeking
          injunctive relief in any court of competent jurisdiction.
        </p>
      </Section>

      <Section title="15. Changes to these Terms">
        <p>
          We may update these Terms as the Service evolves. When we make
          material changes, we will update the effective date above and, where
          appropriate, notify you by email or through the application before
          the change takes effect. Your continued use of the Service after
          the change constitutes acceptance of the updated Terms.
        </p>
      </Section>

      <Section title="16. Miscellaneous">
        <p>
          These Terms, together with our{" "}
          <Link to="/privacy">Privacy Policy</Link>, constitute the entire
          agreement between you and SHOWME regarding the Service and supersede
          any prior agreements. If any provision is held invalid or
          unenforceable, the remaining provisions remain in full force and
          effect. Our failure to enforce any right is not a waiver. You may
          not assign these Terms without our written consent; we may assign
          them in connection with a sale, merger, or other corporate
          transaction. These Terms create no agency, partnership, joint
          venture, or employment relationship between us.
        </p>
      </Section>

      <Section title="17. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:support@showmecampaignfinances.com">
            support@showmecampaignfinances.com
          </a>.
        </p>
      </Section>
    </div>
  );
};

const list = {
  paddingLeft: "1.5rem",
  marginBottom: "1rem"
};

const Section = ({ title, children }) => (
  <section style={{ marginBottom: "2.25rem" }}>
    <h2 style={{
      fontSize: "1.35rem",
      fontWeight: 700,
      color: "#111827",
      marginBottom: "0.75rem"
    }}>
      {title}
    </h2>
    {children}
  </section>
);

export default Terms;
